import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  MutableRefObject,
  ReactElement,
  useMemo,
} from 'react';

import { css, cx } from '@emotion/css';
import styled from '@emotion/styled';
import { overlayColors, RevenueMarketFontFamily, thematicColors } from '@revenuemarket/design';
import { ColDef } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';

import AGGridHeader from './header/AGGridHeader';
import locale from './locale.ko';

// @ts-ignore
const licenseKey = process.env.AG_GRID_LICENSE_KEY;
if (licenseKey) {
  LicenseManager.setLicenseKey(licenseKey);
}

export interface AGGridProps<T> extends AgGridReactProps<T> {
  id?: string;
  containerStyle?: CSSProperties;
  fullPage?: boolean;
}

const AGGridBase = <T,>(
  {
    id,
    defaultColDef: defaultColDefProp,
    fullPage = false,

    ...props
  }: AGGridProps<T>,
  ref: ForwardedRef<AgGridReact>,
) => {
  const defaultColDef = useMemo<ColDef<T>>(
    () => ({
      headerComponent: AGGridHeader,
      ...defaultColDefProp,
    }),
    [defaultColDefProp],
  );

  return (
    <AGGridContainer
      id={id}
      className={cx(
        'ag-theme-quartz',
        css`
          .ag-theme-quartz {
            --ag-font-family: ${RevenueMarketFontFamily} !important;
          }
        `,
        fullPage &&
          css`
            && .ag-column-first {
              &.ag-cell,
              .ag-header-cell-comp-wrapper > * {
                padding-left: 40px;
              }
            }
            && .ag-column-last {
              &.ag-cell,
              .ag-header-cell-comp-wrapper > * {
                padding-right: 40px;
              }
            }
          `,
      )}
      style={{
        ...props.containerStyle,
        fontFamily: RevenueMarketFontFamily,
      }}
    >
      <AgGridReact {...props} ref={ref} localeText={locale} defaultColDef={defaultColDef} />
    </AGGridContainer>
  );
};

export type AGGrid = <T>(
  props: AGGridProps<T> & { ref?: MutableRefObject<AgGridReact | null> },
) => ReactElement;
export const AGGrid = forwardRef(AGGridBase) as AGGrid;

const AGGridContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  --ag-font-family: ${RevenueMarketFontFamily} !important;
  .ag-theme-quartz {
    width: 100%;
    height: 100%;
    --ag-active-color: ${thematicColors.primary.medium};
    // TODO: --ag-foreground-color: antd colorText rgb(0, 0, 0, 0.88);
    --ag-background-color: rgb(255, 255, 255, 0);
    // TODO: --ag-secondary-foreground-color: antd colorIcon rgb(0, 0, 0, 0.45);
    // TODO: --ag-data-color: antd colorText rgb(0, 0, 0, 0.88);
    // TODO: --ag-header-foreground-color: antd colorText rgb(0, 0, 0, 0.88);
    --ag-header-background-color: rgb(0, 0, 0, 0.05);
    --ag-disabled-foreground-color: rgb(0, 0, 0, 0.25);
    --ag-odd-row-background-color: rgb(255, 255, 255, 0);
    --ag-row-hover-color: rgb(0, 0, 0, 0.15);

    --ag-selected-row-background-color: ${overlayColors.primary['2']};

    /* disable all borders */
    --ag-borders: none;

    /* then add back a border between rows */
    --ag-row-border-width: 1px;
    --ag-row-border-color: rgb(0, 0, 0, 0.06);

    --ag-font-size: 13px;
    --ag-font-family: ${RevenueMarketFontFamily} !important;
  }

  .ag-root-wrapper {
    font-size: 12px;
  }

  .ag-header-cell {
    font-size: 12px;
  }

  .ag-cell {
    font-size: 12px;
  }
`;
